/**
 * One button that uploads one image into Media (docs/02 §3, docs/07 §1):
 * ticket, then the presigned PUT, then the media query is refreshed.
 * The composer has its own multi-file chips; this is for the places where a
 * single image is the point (the Media page itself, a brand kit's logo).
 */
import { type ReactNode, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { useUploadImage } from "../api/media";
import { UPLOAD_MIMES } from "../api/types";
import { UploadError } from "../api/uploads";
import { messageFor } from "../copy/errors";
import { Button } from "../ui";

export function errorCodeOf(err: unknown): string {
  if (err instanceof UploadError || err instanceof ApiError) return err.code;
  return "upload_failed";
}

/** The sentence for a failed upload; `storage_full` links to Media,
 *  where the user can free the space (docs/02 §10). */
export function uploadErrorCopy(code: string): ReactNode {
  if (code === "storage_full") {
    return (
      <>
        {messageFor(code)} <Link to="/media">Free up space in Media.</Link>
      </>
    );
  }
  if (code === "upload_failed" || code === "put_failed") {
    return "The upload did not complete. Please try again.";
  }
  return messageFor(code);
}

export function UploadButton({
  label,
  busyLabel = "Uploading…",
  variant = "ghost",
  size,
  testId = "upload-input",
  disabled = false,
  title = "PNG, JPEG or WebP, up to 25 MB",
  onUploaded,
}: {
  label: string;
  busyLabel?: string;
  variant?: "primary" | "ghost";
  size?: "sm";
  /** The hidden file input's test id; the button itself is found by `label`. */
  testId?: string;
  /** No more can be added; `title` then says why ("Up to 6 photos"). */
  disabled?: boolean;
  title?: string;
  /** Called with the new asset id once the bytes are in the blob store. */
  onUploaded?: (assetId: string, file: File) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadImage();
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const onPick = (file: File | undefined) => {
    if (!file || upload.isPending) return;
    setErrorCode(null);
    upload.mutate(file, {
      onSuccess: (assetId) => onUploaded?.(assetId, file),
      onError: (err) => setErrorCode(errorCodeOf(err)),
    });
  };

  return (
    <>
      <input
        ref={input}
        type="file"
        accept={UPLOAD_MIMES.join(",")}
        className="hidden"
        aria-label={label}
        data-testid={testId}
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={upload.isPending || disabled}
        aria-busy={upload.isPending}
        title={title}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? busyLabel : label}
      </Button>
      {errorCode ? (
        <p role="alert" className="m-0 w-full text-sm text-slate-700">
          {uploadErrorCopy(errorCode)}
        </p>
      ) : null}
    </>
  );
}
